import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { CDK_CONNECTED_OVERLAY_DEFAULT_CONFIG, OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom, timeout } from 'rxjs';
import { appRoutes } from './app.routes';
import { AuthService, PermissionService, UsersMeService, httpErrorInterceptor } from '@zag/auth/ui';
import { accessInterceptor, FeatureAccessClientService } from '@zag/features/ui';
import { AUTH_ENTITY_DETAIL_METADATA } from '@zag/auth/ui/lib/entity-detail.metadata';
import { AI_ROUTE_PERMISSION_CHECKER, type AssistantRoutePermissionRequirement } from '@zag/ai/ui';
import {
  BULK_JOBS_GATEWAY,
  FEATURE_CHECKER,
  FIELD_PERMISSION_CHECKER,
  LanguageService,
  ThemeService,
  ENTITY_DETAIL_METADATA,
  languageHeaderInterceptor,
  USER_ID_PROVIDER,
} from '@zag/ui';
import { JobsGatewayClient } from '@zag/jobs/ui';
import { CALENDAR_ENTITY_DETAIL_METADATA } from '@zag/calendar/ui/lib/entity-detail.metadata';
import { PERSON_DETAIL_METADATA } from '@zag/employees/ui/lib/person-edit/person-detail.metadata';
import { LOCATION_DETAIL_METADATA } from '@zag/locations/ui/lib/location-edit/location-detail.metadata';
import { WEBHOOK_DETAIL_METADATA } from '@zag/notifications/ui/lib/webhook-edit/webhook-detail.metadata';
import { PLANNING_ENTITY_DETAIL_METADATA } from '@zag/planning/ui/lib/entity-detail.metadata';
import { provideBreadcrumbConfig } from './breadcrumb.providers';
import { APP_ENTITY_DETAIL_METADATA } from './entity-detail.metadata';

const ENTITY_DETAIL_METADATA_LOADERS = [
  () => import('@zag/documents/ui').then(module => module.DOCUMENT_TYPE_DETAIL_METADATA),
  () => import('@zag/projects/ui').then(module => module.PROJECT_DETAIL_METADATA),
  () => import('@zag/timetracking/ui').then(module => module.TIMETRACKING_ENTITY_DETAIL_METADATA),
];
import { TenantStatusService } from './tenant-status.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: CDK_CONNECTED_OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    provideRouter(appRoutes, withRouterConfig({ canceledNavigationResolution: 'computed' })),
    { provide: BULK_JOBS_GATEWAY, useExisting: JobsGatewayClient },
    ...[
      ...APP_ENTITY_DETAIL_METADATA,
      ...AUTH_ENTITY_DETAIL_METADATA,
      ...CALENDAR_ENTITY_DETAIL_METADATA,
      ...LOCATION_DETAIL_METADATA,
      ...PERSON_DETAIL_METADATA,
      ...PLANNING_ENTITY_DETAIL_METADATA,
      ...WEBHOOK_DETAIL_METADATA,
    ].map(metadata => ({ provide: ENTITY_DETAIL_METADATA, useValue: metadata, multi: true })),
    ...ENTITY_DETAIL_METADATA_LOADERS.map(loadMetadata => ({
      provide: ENTITY_DETAIL_METADATA,
      useValue: loadMetadata,
      multi: true,
    })),
    {
      provide: AI_ROUTE_PERMISSION_CHECKER,
      useFactory: () => {
        const permissionService = inject(PermissionService);
        return (requirement: AssistantRoutePermissionRequirement) =>
          requirement.mode === 'all'
            ? permissionService.hasAllPermissions([...requirement.permissions])
            : permissionService.hasAnyPermission([...requirement.permissions]);
      },
    },
    { provide: FIELD_PERMISSION_CHECKER, useExisting: PermissionService },
    { provide: FEATURE_CHECKER, useExisting: FeatureAccessClientService },
    {
      provide: USER_ID_PROVIDER,
      useFactory: () => {
        const auth = inject(AuthService);
        return () => auth.currentUser()?.id;
      },
    },
    provideBreadcrumbConfig(),
    // Provide HttpClient at the top level so the interceptors are actually
    // applied. The Accept-Language interceptor stamps every request with the
    // user's currently selected UI language so the backend can resolve
    // per-entity translations correctly.
    provideHttpClient(withXhr(), withInterceptors([languageHeaderInterceptor, accessInterceptor, httpErrorInterceptor])),
    // Why: fallbackLang triggers a constructor-time request before subscribers are wired.
    provideTranslateService({}),
    provideTranslateHttpLoader({
      prefix: '/api/v1/i18n/',
      suffix: '',
    }),
    // Load translations with browser language detection
    provideAppInitializer(async () => {
      const translate = inject(TranslateService);
      const languageService = inject(LanguageService);

      try {
        // Initialize language (detects browser language or uses stored preference)
        const language = languageService.initializeLanguage();

        // Set fallback language and load active-language translations after the
        // service is fully constructed (avoids constructor-time race condition).
        translate.setFallbackLang('en');
        await firstValueFrom(translate.use(language).pipe(timeout(5000)));
      } catch (error) {
        console.warn('Failed to load translations, using keys as fallback:', error);
      }
    }),
    // Initialize theme (detect system preference or use stored preference)
    provideAppInitializer(() => {
      const themeService = inject(ThemeService);

      // Initialize theme from localStorage or system preference
      const theme = themeService.initializeTheme();

      // Apply theme to document
      themeService.applyThemeToDocument(theme);

      // Add theme transition class to root for smooth transitions
      if (typeof document !== 'undefined') {
        document.documentElement.classList.add('theme-transition');
      }
    }),
    // Check for existing session on app startup.
    // Skip the session check when the browser is on a tenant subdomain that
    // is not yet provisioned — the main API's auth endpoint will return 404
    // or 500 for unprovisioned tenants, which delays Angular bootstrap
    // unnecessarily. The provisioning guard will redirect to /provisioning
    // and the session will be loaded after the tenant is ready.
    provideAppInitializer(async () => {
      const authService = inject(AuthService);
      const usersMeService = inject(UsersMeService);
      const tenantStatusService = inject(TenantStatusService);

      // When on a tenant domain, check tenant status first to avoid
      // hitting the main API when the tenant is not yet provisioned.
      if (tenantStatusService.host) {
        const resource = tenantStatusService.statusResource;

        // Wait for the status resource to settle (max 3 seconds).
        const UNSETTLED = new Set(['idle', 'loading', 'reloading']);
        const deadline = Date.now() + 3000;
        while (UNSETTLED.has(resource.status()) && Date.now() < deadline) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        if (tenantStatusService.status() !== 'ready') {
          console.debug('Tenant not ready, skipping session check');
          return;
        }
      }

      try {
        await authService.getSession();
        // Sync server-side language/theme preferences (best-effort, non-blocking)
        usersMeService.syncServerPreferences();
      } catch {
        // Session check failed - user not logged in, this is fine
        console.debug('No active session found');
      }
    }),
  ],
};
